import {
  createRotationTracker, gravityPath, simulateGravity,
  gravityLeanDegrees, type LeanReading,
  type RotationCalibrationResult, type RotationMounting, type RotationTrackerSnapshot, type Vec3,
} from '@agavi/blocks';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The gravity half of the Angle block's sensor stack: browser plumbing only, with all of the
 * measurement maths living in `@agavi/blocks`' `rotation-tracking.ts`.
 *
 * Two hooks ship from here. `useDeviceGravity` is the generic piece — permission, support
 * detection, filtering, stream-health — and deliberately hands samples to a callback rather than
 * to React state, because the stream arrives at ~60Hz and a state update per sample would re-render
 * the whole editor sixty times a second. `useRotationSensor` composes it with a tracker and publishes a
 * throttled snapshot, which is what a component actually wants.
 *
 * WHY DeviceMotionEvent AND NOT DeviceOrientationEvent
 *
 * Compass reads `DeviceOrientationEvent` because it wants a heading, which iOS pre-fuses into
 * `webkitCompassHeading`. Angle wants the gravity VECTOR, and the recorded design is explicit that
 * it must come from the vector rather than from `beta`/`gamma`: those are Euler angles, and `gamma`
 * jumps or flips sign as `beta` crosses +/-90 — a gimbal-lock-adjacent artefact that would corrupt
 * readings in exactly the steep attitudes this block exists to measure.
 * `DeviceMotionEvent.accelerationIncludingGravity` gives the raw vector with no such seam.
 */

/** Standard gravity, for the plausibility gate below. */
const G = 9.80665;

/**
 * Exponential low-pass coefficient. `accelerationIncludingGravity` is gravity PLUS whatever the
 * hand is doing, so the sample has to be smoothed before it can be treated as a gravity direction.
 * At a typical 60Hz stream this is a time constant near 110ms: long enough to reject hand tremor,
 * short enough that the readout still tracks a deliberate rotation without visible lag.
 */
const LOW_PASS_ALPHA = 0.85;

/**
 * A sample is only treated as gravity when its magnitude is close to 1g. Under real linear
 * acceleration — a phone being swung, jostled or dropped — the vector is the sum of gravity and that
 * acceleration, and its direction is not "down" at all. Discarding those samples is better than
 * smoothing them in, because the filter would carry the error forward for its whole time constant.
 */
const PLAUSIBLE_LO = 0.6 * G;
const PLAUSIBLE_HI = 1.4 * G;

/**
 * How long the stream may go quiet before the turn count is written off. Unwrapping past +/-180
 * needs contiguous samples; a gap means a rotation could have happened unobserved, so the tracker is
 * told the stream broke rather than being allowed to keep a stale count.
 */
const SAMPLE_GAP_MS = 700;

/**
 * `'DeviceMotionEvent' in window` is true on desktop browsers with no accelerometer, and they will
 * happily fire events carrying nulls — exactly the trap Compass documented for
 * `deviceorientation`. So support is decided by whether a USABLE sample ever arrives, not by
 * feature detection. Matches Compass's own DEVICE_HEADING_TIMEOUT_MS.
 */
const SAMPLE_TIMEOUT_MS = 4000;

/** Display refresh rate for the published snapshot. The tracker still sees every sample. */
const PUBLISH_INTERVAL_MS = 100;

const MOTION_SUPPORTED = typeof window !== 'undefined' && 'DeviceMotionEvent' in window;

/**
 * Which way round `accelerationIncludingGravity` points, per platform.
 *
 * THE ONE UNTESTED ASSUMPTION IN THIS FILE, and the one most worth getting right, because being
 * wrong here is a silent 180 degree error — precisely the failure this block's whole design exists
 * to prevent. Verify it on a real device before trusting any reading.
 *
 * The W3C convention reports the acceleration the device is UNDERGOING, so a phone resting flat with
 * the screen up reads about +9.81 on z: the table is pushing it up at 1g relative to free fall. The
 * gravity DIRECTION is therefore the negation. iOS Safari has always reported the opposite sign on
 * all three axes, so there the raw vector already points the way gravity does.
 *
 * Detected by capability rather than by user agent: `requestPermission` on DeviceMotionEvent is
 * implemented only by iOS Safari, which makes it an exact proxy for the platform that inverts, and a
 * far sturdier one than parsing a UA string. If a future iOS release aligns with the spec, this is
 * the single line to change.
 *
 * How to check on a device: lay the phone flat, screen up, and press Calibrate. The readout must say
 * 0 degrees. If it says 180, this constant has the wrong sign.
 */
function accelSign(): 1 | -1 {
  if (typeof window === 'undefined' || !MOTION_SUPPORTED) return -1;
  const Ctor = window.DeviceMotionEvent as unknown as { requestPermission?: () => Promise<'granted' | 'denied'> };
  return typeof Ctor?.requestPermission === 'function' ? 1 : -1;
}

export type GravityStatus =
  /** Not asked for yet. */
  | 'idle'
  /** Awaiting the iOS permission prompt. */
  | 'requesting'
  /** The user said no. */
  | 'denied'
  /** No DeviceMotionEvent at all, or it never produced a usable sample — i.e. a desktop. */
  | 'unsupported'
  /**
   * The page is not a secure context, so the sensor APIs are withheld by the browser regardless of
   * hardware. Called out separately from `unsupported` because it is the expected state when
   * testing on a phone against a plain-HTTP dev server, and "your phone can't do this" would be a
   * actively misleading thing to say about a phone that can.
   */
  | 'insecure-context'
  /** Permission is in hand, but no usable sample has arrived yet. */
  | 'waiting'
  /** Samples are flowing. */
  | 'live';

interface DeviceGravityOptions {
  active: boolean;
  /** Called for every usable, filtered sample. Kept out of React state on purpose. */
  onSample: (gravity: Vec3) => void;
  /** Called when contiguity is lost: a sample gap, or the page being hidden. */
  onStreamBreak: () => void;
}

interface DeviceGravityResult {
  status: GravityStatus;
  /** Triggers the iOS permission prompt. MUST be called from a user gesture. Resolves true when
   * samples may now be expected. Safe to call on platforms with no prompt, where it is a no-op. */
  request: () => Promise<boolean>;
}

type MotionCtor = { requestPermission?: () => Promise<'granted' | 'denied'> };

export function useDeviceGravity({ active, onSample, onStreamBreak }: DeviceGravityOptions): DeviceGravityResult {
  const [status, setStatus] = useState<GravityStatus>('idle');
  const [granted, setGranted] = useState(false);

  // Callbacks live in refs so that a caller passing inline arrow functions — which every caller
  // will — does not tear down and rebuild the listener on every render.
  const onSampleRef = useRef(onSample);
  const onBreakRef = useRef(onStreamBreak);
  onSampleRef.current = onSample;
  onBreakRef.current = onStreamBreak;

  const request = useCallback(async (): Promise<boolean> => {
    if (typeof window === 'undefined') return false;
    if (!window.isSecureContext) { setStatus('insecure-context'); return false; }
    if (!MOTION_SUPPORTED) { setStatus('unsupported'); return false; }

    const Ctor = window.DeviceMotionEvent as unknown as MotionCtor;
    // Only iOS gates this, and only from a user gesture — hence a function the click handler calls
    // rather than a request buried in an effect.
    if (typeof Ctor?.requestPermission === 'function') {
      setStatus('requesting');
      try {
        if ((await Ctor.requestPermission()) !== 'granted') { setStatus('denied'); return false; }
      } catch {
        setStatus('denied');
        return false;
      }
    }
    setGranted(true);
    setStatus('waiting');
    return true;
  }, []);

  useEffect(() => {
    if (!active || !granted) return;
    if (typeof window === 'undefined') return;
    if (!window.isSecureContext) { setStatus('insecure-context'); return; }
    if (!MOTION_SUPPORTED) { setStatus('unsupported'); return; }

    const k = accelSign();
    let filtered: Vec3 | null = null;
    let lastSampleAt = 0;
    let sawUsableSample = false;

    const onMotion = (e: DeviceMotionEvent) => {
      const a = e.accelerationIncludingGravity;
      if (!a || a.x == null || a.y == null || a.z == null) return;

      const magnitude = Math.hypot(a.x, a.y, a.z);
      if (!Number.isFinite(magnitude) || magnitude < PLAUSIBLE_LO || magnitude > PLAUSIBLE_HI) return;

      const now = Date.now();
      if (lastSampleAt !== 0 && now - lastSampleAt > SAMPLE_GAP_MS) {
        // The stream stalled and resumed. Whatever the phone did in between is unobserved, so the
        // filter restarts cold and the tracker is told its turn count is worthless.
        filtered = null;
        onBreakRef.current();
      }
      lastSampleAt = now;

      // Device axes: x across the screen, y up the screen, z out through the glass. `k` resolves the
      // platform sign disagreement documented on accelSign; after it, `sample` always points the way
      // gravity does, which is what rotation-tracking.ts expects.
      const sample: Vec3 = { x: k * a.x, y: k * a.y, z: k * a.z };
      filtered = filtered
        ? {
            x: filtered.x * LOW_PASS_ALPHA + sample.x * (1 - LOW_PASS_ALPHA),
            y: filtered.y * LOW_PASS_ALPHA + sample.y * (1 - LOW_PASS_ALPHA),
            z: filtered.z * LOW_PASS_ALPHA + sample.z * (1 - LOW_PASS_ALPHA),
          }
        : sample;

      if (!sawUsableSample) {
        sawUsableSample = true;
        setStatus('live');
      }
      onSampleRef.current(filtered);
    };

    // A hidden page stops delivering motion events, so anything that happened while it was away is
    // unobserved. This is the interruption case the design flagged as unsolved: without it, a phone
    // set down mid-gesture would resume with a stale turn count and capture a confident wrong value.
    const onVisibility = () => {
      if (document.hidden) {
        filtered = null;
        lastSampleAt = 0;
        onBreakRef.current();
      }
    };

    window.addEventListener('devicemotion', onMotion);
    document.addEventListener('visibilitychange', onVisibility);

    const timer = setTimeout(() => {
      if (!sawUsableSample) setStatus('unsupported');
    }, SAMPLE_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('devicemotion', onMotion);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, granted]);

  return { status, request };
}

/**
 * A phone that is not there, as a sample source for the same tracker the real one feeds.
 *
 * A PLAY rather than a live attitude: the whole gesture is set up in advance and performed when
 * Calibrate is pressed. That is what lets the panel be a modal — nothing has to be watched while it
 * is open — and it makes a run reproducible, where a live dial made the result depend on the path a
 * hand happened to take.
 *
 * Nothing downstream knows the samples are invented. Latch, fold resolution, gesture
 * classification, off-axis drift and stream breaks are all derived from the stream rather than
 * declared, which is what lets the simulator test the tracker instead of standing in for it.
 */
export interface SimulatedGravity {
  mounting: RotationMounting;
  /** Where the phone is when Calibrate is pressed. */
  startDegrees: number;
  /** Where it ends up, reached in one continuous sweep from the start. */
  endDegrees: number;
  offAxisDegrees: number;
  /** Peak accelerometer noise as a fraction of g — what a poor signal looks like to this sensor. */
  noise: number;
  /** No samples at all. The device is on, and reporting nothing. */
  silent: boolean;
  /** The stream keeps breaking, the way a phone that sleeps mid-gesture does. Held rather than
   * pulsed, so the fault is a condition a teacher arms and disarms rather than a moment they have
   * to time — and so the same setup fails the same way twice. */
  interrupted: boolean;
}

export interface RotationSensorResult {
  status: GravityStatus;
  /** Throttled to PUBLISH_INTERVAL_MS. Null before the first sample. */
  snapshot: RotationTrackerSnapshot | null;
  /** Prompts for permission if needed, then calibrates against the newest sample. Call from a click
   * handler — the iOS prompt requires a user gesture. */
  calibrate: () => Promise<RotationCalibrationResult>;
  /** Drops the reference sample and every derived value. */
  reset: () => void;
}

/**
 * The tracker wired to the device, publishing at a rate a UI can render.
 *
 * The tracker sees every sample so that unwrapping past +/-180 cannot alias; the component sees a
 * snapshot ten times a second. Keeping those rates separate is the whole reason this hook exists
 * rather than the block reading `useDeviceGravity` directly.
 */
export function useRotationSensor(active: boolean, simulated?: SimulatedGravity | null): RotationSensorResult {
  const trackerRef = useRef(createRotationTracker());
  const latestRef = useRef<Vec3 | null>(null);
  const pendingRef = useRef<RotationTrackerSnapshot | null>(null);
  const [snapshot, setSnapshot] = useState<RotationTrackerSnapshot | null>(null);
  /** Either source counts as running. The device path also wants its listener attached, which the
   * simulated one has no equivalent of, so only `active` reaches `useDeviceGravity`. */
  const running = active || simulated != null;

  const { status: deviceStatus, request } = useDeviceGravity({
    active,
    onSample: (gravity) => {
      latestRef.current = gravity;
      pendingRef.current = trackerRef.current.update(gravity);
    },
    onStreamBreak: () => {
      trackerRef.current.reportStreamBreak();
      pendingRef.current = trackerRef.current.snapshot();
    },
  });

  /**
   * A change to the PLAY voids the gesture, rather than being performed on the spot.
   *
   * Nothing streams until Calibrate runs it. Resetting here is what makes that legible: move the
   * start, the end or the arrangement and the block goes back to no reading, which is a truthful
   * account of a phone whose gesture has been abandoned — and it stops the block reporting the
   * result of a play that is no longer the one on screen.
   */
  const playKey = simulated
    && `${simulated.mounting}:${simulated.startDegrees}:${simulated.endDegrees}:${simulated.offAxisDegrees}:${simulated.silent}`;
  const lastPlayRef = useRef<string | null>(null);
  useEffect(() => {
    if (!playKey) { lastPlayRef.current = null; return; }
    if (lastPlayRef.current !== null && lastPlayRef.current !== playKey) {
      trackerRef.current.reset();
      latestRef.current = null;
      pendingRef.current = null;
      setSnapshot(null);
    }
    lastPlayRef.current = playKey;
  }, [playKey]);

  /**
   * An interruption is a condition rather than a play, so it applies the moment it is armed and
   * keeps applying — a recalibration cannot quietly recover while it is on, which is what a phone
   * with a real interruption problem does.
   */
  const interrupted = !!simulated?.interrupted;
  /** Re-runs after anything that could have re-latched, so an armed interruption keeps biting. */
  const recovered = snapshot?.state !== 'stream-broken';
  useEffect(() => {
    if (!interrupted || !recovered) return;
    trackerRef.current.reportStreamBreak();
    pendingRef.current = trackerRef.current.snapshot();
    setSnapshot(pendingRef.current);
  }, [interrupted, recovered]);

  // One interval, not a state update per sample. `pendingRef` always holds the newest snapshot, so
  // publishing on a timer costs nothing and loses nothing but intermediate frames.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      if (pendingRef.current) setSnapshot(pendingRef.current);
    }, PUBLISH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (running) return;
    // Leaving the surface discards the gesture rather than freezing it, so returning later cannot
    // capture against a reference sample taken before the phone was put in a pocket.
    trackerRef.current.reset();
    latestRef.current = null;
    pendingRef.current = null;
    setSnapshot(null);
  }, [running]);

  /**
   * Waits for the first usable sample after a permission grant.
   *
   * Without this, the very first Calibrate press on a working phone fails. `request()` resolves as
   * soon as the user accepts the iOS prompt, but the listener is attached by an effect that has not
   * run yet, so no sample exists and the block would conclude there is no sensor — then samples would
   * start arriving, the block would switch to the sensor path, find no reference sample there, and
   * the calibration the student just performed would silently undo itself.
   *
   * Two seconds is generous: `devicemotion` fires at tens of hertz the moment it is attached, so
   * anything approaching this deadline means the device genuinely is not delivering, which is exactly
   * the answer the caller wants in that case.
   */
  const waitForSample = useCallback(async (timeoutMs = 2000): Promise<Vec3 | null> => {
    if (latestRef.current) return latestRef.current;
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (latestRef.current) return latestRef.current;
    }
    return null;
  }, []);

  const calibrate = useCallback(async (): Promise<RotationCalibrationResult> => {
    // A simulated stream needs no permission and is never waiting: the sample is whatever the dial
    // is set to right now, or nothing at all when the device is reporting no signal.
    /**
     * Calibrate RUNS the play: anchor at the start, then sweep to the end in one continuous motion,
     * which is the gesture a student performs and the only thing the tracker can resolve a fold
     * from. One press produces the whole reading, so the panel never has to be open at the same
     * time as the block.
     */
    if (simulated) {
      if (simulated.silent) return { ok: false, reason: 'no-signal' };
      const start = { ...simulated, degrees: simulated.startDegrees };
      const g = simulateGravity(start);
      const result = trackerRef.current.calibrate(g);
      if (result.ok) {
        latestRef.current = g;
        pendingRef.current = trackerRef.current.update(g);
        for (const sample of gravityPath(start, simulated.endDegrees)) {
          latestRef.current = sample;
          pendingRef.current = trackerRef.current.update(sample);
        }
      } else {
        pendingRef.current = trackerRef.current.snapshot();
      }
      setSnapshot(pendingRef.current);
      return result;
    }
    const permitted = await request();
    const g = permitted ? await waitForSample() : latestRef.current;
    if (!g) return { ok: false, reason: 'no-signal' };
    const result = trackerRef.current.calibrate(g);
    pendingRef.current = trackerRef.current.snapshot();
    setSnapshot(pendingRef.current);
    return result;
  }, [request, waitForSample, simulated]);

  const reset = useCallback(() => {
    trackerRef.current.reset();
    pendingRef.current = null;
    setSnapshot(null);
  }, []);

  /** A running simulator IS the device as far as the block is concerned — it is delivering samples,
   * so anything gated on a live sensor should run. `unsupported` when it is silent, which is the
   * same answer a desktop gives and the same one the block already knows how to explain. */
  const status: GravityStatus = simulated ? (simulated.silent ? 'unsupported' : 'live') : deviceStatus;
  return { status, snapshot, calibrate, reset };
}

// ─── Tilt ─────────────────────────────────────────────────────────────────────

export interface TiltSensorResult {
  status: GravityStatus;
  /** The lean and its off-axis component, throttled to PUBLISH_INTERVAL_MS. Null before the
   *  first sample. The off-axis figure travels WITH the measurement because the one way to hold
   *  the phone wrong — diagonally — produces a plausible number rather than a failure. */
  reading: LeanReading | null;
  /** Prompts for permission if needed. Call from a click handler — iOS requires a user gesture.
   *  Resolves true when samples may now be expected. */
  request: () => Promise<boolean>;
}

/**
 * Gravity, read as a pose. The whole of Tilt's sensor layer.
 *
 * Deliberately NOT `useRotationSensor` with the tracker swapped out. That hook exists because a
 * swept angle needs every sample (so unwrapping past 180 cannot alias) while the UI wants ten a
 * second, and it carries a calibration, a reference attitude, a latch and a stream-break protocol
 * to make a PATH trustworthy. A pose has no path: each sample is independently the whole answer,
 * so there is nothing to calibrate against, nothing to latch and nothing an interruption can
 * corrupt — the next sample is as good as the last.
 *
 * What survives is the throttle, for the same reason: publishing every sample would re-render the
 * editor sixty times a second.
 *
 * `simulatedGravity` feeds the same arithmetic in place of the device, rather than short-circuiting
 * to a number — see `tiltGravityFor`.
 */
export function useTiltSensor(
  active: boolean,
  simulatedGravity?: Vec3 | null,
): TiltSensorResult {
  const pendingRef = useRef<LeanReading | null>(null);
  const [reading, setReading] = useState<LeanReading | null>(null);
  const running = active || simulatedGravity != null;

  /**
   * The screen's own orientation decides which device axis is left-right, so the student can hold
   * the phone whichever way they like. Read per sample rather than cached: a phone turned
   * mid-measurement should follow the hand, not the attitude it started in.
   */
  const readFrom = useCallback((g: Vec3) => {
    const angle = typeof window === 'undefined' ? 0 : (window.screen?.orientation?.angle ?? 0);
    pendingRef.current = gravityLeanDegrees(g, angle);
  }, []);

  const { status: deviceStatus, request } = useDeviceGravity({
    active: active && simulatedGravity == null,
    onSample: readFrom,
    // A pose has no contiguity to lose. Each sample is independently the whole answer, so a gap
    // costs the reading nothing — unlike a tracked path, where a missed stretch is a missed turn.
    onStreamBreak: () => {},
  });

  // The simulated sample is a value rather than a stream, so it publishes on change instead of
  // on the timer.
  useEffect(() => {
    if (simulatedGravity == null) return;
    readFrom(simulatedGravity);
    setReading(pendingRef.current);
  }, [simulatedGravity, readFrom]);

  useEffect(() => {
    if (!running || simulatedGravity != null) return;
    const id = setInterval(() => setReading(pendingRef.current), PUBLISH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [running, simulatedGravity]);

  useEffect(() => {
    if (running) return;
    pendingRef.current = null;
    setReading(null);
  }, [running]);

  /*
   * A RUNNING SIMULATOR IS THE DEVICE, as far as the block is concerned.
   *
   * The same line the rotation tracker above already carries, and Tilt was written without it.
   * The device hook is deliberately inactive while a simulated sample is driving — there is
   * nothing to listen to — so its status is `idle`, and this returned that. The block gates its
   * readout on `status === 'live'`, so the simulated lean was computed, published into
   * `reading`, and then thrown away one line later by `sensorLive ? reading : null`.
   *
   * Which is exactly what "the simulator does not send tilt data to the block" looks like: the
   * dial moves, the sample arrives, and the block shows nothing.
   */
  const status: GravityStatus = simulatedGravity != null ? 'live' : deviceStatus;
  return { status, reading, request };
}
